import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { User, Globe, Tag, Bell, CreditCard, Save } from 'lucide-react';
import { toast } from 'sonner';
import theme from '../theme';
import ResetTourButton from '../components/ResetTourButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputStyle = {
  width: '100%', padding: '12px', background: theme.colors.bg.primary,
  border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm,
  color: theme.colors.text.primary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
};

const CATEGORY_OPTIONS = ['Apparel', 'Accessories', 'Footwear', 'Art', 'Music', 'Collectibles', 'Sports', 'Other'];

const CreatorSettingsPageWorking = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    display_name: '', email: '', brand_name: '', website: '',
    categories: [],
    notification_prefs: { portfolio_ready: true, launch_events: true, payout_cycles: true },
    payout_prefs: { method: 'USD_BANK', currency: 'USD' },
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/creator/settings`);
      setForm({
        display_name: res.data.display_name || '',
        email: res.data.email || '',
        brand_name: res.data.brand_name || '',
        website: res.data.website || '',
        categories: res.data.categories || [],
        notification_prefs: res.data.notification_prefs || { portfolio_ready: true, launch_events: true, payout_cycles: true },
        payout_prefs: res.data.payout_prefs || { method: 'USD_BANK', currency: 'USD' },
      });
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/creator/settings`, {
        name: form.display_name,
        brand_name: form.brand_name,
        website: form.website,
        categories: form.categories,
        notification_prefs: form.notification_prefs,
        payout_prefs: form.payout_prefs,
      });
      toast.success('Settings saved!');
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleCat = (cat) => {
    setForm(f => ({
      ...f,
      categories: f.categories.includes(cat)
        ? f.categories.filter(c => c !== cat)
        : [...f.categories, cat],
    }));
  };

  const toggleNotif = (key) => setForm(f => ({
    ...f, notification_prefs: { ...f.notification_prefs, [key]: !f.notification_prefs[key] },
  }));

  if (loading) return <div style={{ padding: '40px', color: theme.colors.text.muted }}>Loading settings...</div>;

  return (
    <div data-testid="creator-settings-page" style={{ padding: '40px', maxWidth: '800px' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '8px' }}>Creator Settings</h1>
      <p style={{ color: theme.colors.text.secondary, fontSize: '15px', marginBottom: '32px' }}>Manage your account and brand preferences</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Profile */}
        <div style={{ background: theme.colors.bg.card, borderRadius: theme.radius.card, padding: '24px', border: `1px solid ${theme.colors.borders}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <User size={20} color={theme.colors.primary} />
            <h3 style={{ color: theme.colors.text.primary, fontSize: '18px', fontWeight: 600 }}>Profile</h3>
          </div>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', color: theme.colors.text.muted, fontSize: '13px', marginBottom: '6px' }}>Display Name</label>
              <input data-testid="creator-display-name" type="text" value={form.display_name} maxLength={80}
                onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ display: 'block', color: theme.colors.text.muted, fontSize: '13px', marginBottom: '6px' }}>Email</label>
              <input data-testid="creator-email" type="email" value={form.email} disabled style={{ ...inputStyle, color: theme.colors.text.muted }} />
            </div>
          </div>
        </div>

        {/* Brand */}
        <div style={{ background: theme.colors.bg.card, borderRadius: theme.radius.card, padding: '24px', border: `1px solid ${theme.colors.borders}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Globe size={20} color={theme.colors.primary} />
            <h3 style={{ color: theme.colors.text.primary, fontSize: '18px', fontWeight: 600 }}>Brand</h3>
          </div>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', color: theme.colors.text.muted, fontSize: '13px', marginBottom: '6px' }}>Brand Name</label>
              <input data-testid="creator-brand-name" type="text" value={form.brand_name} maxLength={80}
                onChange={e => setForm(f => ({ ...f, brand_name: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ display: 'block', color: theme.colors.text.muted, fontSize: '13px', marginBottom: '6px' }}>Website</label>
              <input data-testid="creator-website" type="text" value={form.website} maxLength={200}
                onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." style={inputStyle} />
            </div>
          </div>
        </div>

        {/* Categories */}
        <div style={{ background: theme.colors.bg.card, borderRadius: theme.radius.card, padding: '24px', border: `1px solid ${theme.colors.borders}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Tag size={20} color={theme.colors.primary} />
            <h3 style={{ color: theme.colors.text.primary, fontSize: '18px', fontWeight: 600 }}>Categories</h3>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {CATEGORY_OPTIONS.map(cat => (
              <button key={cat} data-testid={`cat-${cat.toLowerCase()}`} onClick={() => toggleCat(cat)} style={{
                padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                background: form.categories.includes(cat) ? `${theme.colors.primary}20` : 'transparent',
                color: form.categories.includes(cat) ? theme.colors.primary : theme.colors.text.muted,
                border: `1px solid ${form.categories.includes(cat) ? theme.colors.primary : theme.colors.borders}`,
              }}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Notifications */}
        <div style={{ background: theme.colors.bg.card, borderRadius: theme.radius.card, padding: '24px', border: `1px solid ${theme.colors.borders}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Bell size={20} color={theme.colors.primary} />
            <h3 style={{ color: theme.colors.text.primary, fontSize: '18px', fontWeight: 600 }}>Notifications</h3>
          </div>
          {[
            { key: 'portfolio_ready', label: 'Portfolio ready alerts' },
            { key: 'launch_events', label: 'Launch event notifications' },
            { key: 'payout_cycles', label: 'Payout cycle updates' },
          ].map(({ key, label }) => (
            <label key={key} data-testid={`notif-${key}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
              <input type="checkbox" checked={form.notification_prefs[key] || false} onChange={() => toggleNotif(key)}
                style={{ width: '18px', height: '18px' }} />
              <span style={{ color: theme.colors.text.primary, fontSize: '14px' }}>{label}</span>
            </label>
          ))}
        </div>

        {/* Payout */}
        <div style={{ background: theme.colors.bg.card, borderRadius: theme.radius.card, padding: '24px', border: `1px solid ${theme.colors.borders}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <CreditCard size={20} color={theme.colors.primary} />
            <h3 style={{ color: theme.colors.text.primary, fontSize: '18px', fontWeight: 600 }}>Payout Preferences</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', color: theme.colors.text.muted, fontSize: '13px', marginBottom: '6px' }}>Method</label>
              <select data-testid="creator-payout-method" value={form.payout_prefs.method}
                onChange={e => setForm(f => ({ ...f, payout_prefs: { ...f.payout_prefs, method: e.target.value } }))} style={inputStyle}>
                <option value="USD_BANK">USD Bank Transfer</option>
                <option value="PAYPAL">PayPal</option>
                <option value="CRYPTO">Crypto Wallet</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', color: theme.colors.text.muted, fontSize: '13px', marginBottom: '6px' }}>Currency</label>
              <select data-testid="creator-payout-currency" value={form.payout_prefs.currency}
                onChange={e => setForm(f => ({ ...f, payout_prefs: { ...f.payout_prefs, currency: e.target.value } }))} style={inputStyle}>
                <option value="USD">USD</option>
                <option value="USDC">USDC</option>
                <option value="ETH">ETH</option>
              </select>
            </div>
          </div>
        </div>

        <button data-testid="creator-save-settings-btn" onClick={handleSave} disabled={saving} style={{
          padding: '14px 28px', background: saving ? theme.colors.text.muted : theme.colors.primary,
          color: '#FFF', border: 'none', borderRadius: theme.radius.sm, fontSize: '15px', fontWeight: 600,
          cursor: saving ? 'not-allowed' : 'pointer', width: 'fit-content',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <ResetTourButton user={user} />
      </div>
    </div>
  );
};

export default CreatorSettingsPageWorking;
